import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../../assets/Icons";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import { taxCompliantAccountDetail } from "../../../../../redux/features/accounts/taxAccount/taxAccountDetails";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { toast_position } from "../../../../../redux/features/Settings/Authorization";
import accountNameStandardised from "../../../../../utils/accountNamestandardised";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";

const TaxationEditAccountSuccess = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { id, successType } = useParams();
  const updatedFields = location?.state?.updatedFields || [];
  const [currentAccountDetails, setCurrentAccountDetails] = useState({
    id: "",
    name: "",
    bank_name: "",
    bank_acc_no: "",
    ifsc_code: "",
    category: "",
    pan: "",
    labels: "",
  });

  const { taxAccountEditOtherDetailsData } = useSelector(
    (state) => state.verify,
  );

  const accountData = taxAccountEditOtherDetailsData?.tds_accounts[0];

  const contactFieldConfig = {
    account_name: {
      leftSubTitle: "Account name",
      getTitle: (acc) =>
        acc?.name ? accountNameStandardised(acc.name) : "Not available",
    },
    contact_no: {
      leftSubTitle: "Contact number",
      icon: ICONS.CONTACT_NO,
      getTitle: (acc) =>
        acc?.contact_no ? `+91 ${acc.contact_no}` : "Not available",
    },
    email_id: {
      leftSubTitle: "Email ID",
      icon: ICONS.GMAIL_RED_ERROR,
      getTitle: (acc) => acc?.email_id || "Not available",
    },
  };

  const categoryFieldConfig = {
    category: {
      leftSubTitle: "Tax type",
      getTitle: (acc) => acc?.category || "Not assigned",
    },
  };

  const getFieldsToShow = () => {
    if (updatedFields.length > GlobalConfiguration.lengthCheckZero) {
      return updatedFields;
    }
    if (successType === "tax_contact_edit_success") {
      return ["account_name", "contact_no", "email_id"];
    }
    if (successType === "taxType_edit_success") {
      return ["category"];
    }
    return null;
  };

  const fieldsToShow = getFieldsToShow();
  const showOnlyUpdated =
    fieldsToShow &&
    (successType === "tax_contact_edit_success" ||
      successType === "taxType_edit_success");

  const getSuccessTitle = () => {
    if (successType === "taxType_edit_success") {
      return "Tax type updated successfully!";
    }
    if (successType === "tax_contact_edit_success") {
      const hasContactUpdate = updatedFields.some((f) =>
        ["contact_no", "email_id"].includes(f),
      );
      const hasNameUpdate = updatedFields.includes("account_name");
      if (hasNameUpdate && !hasContactUpdate)
        return "Account name updated successfully.";
      if (hasContactUpdate && !hasNameUpdate)
        return "Contact details updated successfully.";
      if (hasNameUpdate && hasContactUpdate)
        return "Account details updated successfully.";
    }
    return "Details updated successfully.";
  };

  const renderInfoCards = () => {
    if (successType === "tax_contact_edit_success") {
      return fieldsToShow
        .filter((key) => contactFieldConfig[key])
        .map((key) => {
          const config = contactFieldConfig[key];
          return (
            <InfoCard
              key={key}
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: config.getTitle(accountData),
                  leftSubTitle: config.leftSubTitle,
                  ...(config.icon && { icon: config.icon }),
                },
              ]}
            />
          );
        });
    }
    if (successType === "taxType_edit_success") {
      return fieldsToShow
        .filter((key) => categoryFieldConfig[key])
        .map((key) => {
          const config = categoryFieldConfig[key];
          return (
            <InfoCard
              key={key}
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: config.getTitle(accountData),
                  leftSubTitle: config.leftSubTitle,
                },
              ]}
            />
          );
        });
    }
    return null;
  };

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (accountData) {
      const {
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category,
        pan_no,
        labels,
      } = accountData;
      setCurrentAccountDetails({
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category,
        pan: pan_no,
        labels: labels,
      });
    }
  }, [accountData]);

  const handlers = {
    createAccount: () => navigate("/tax_account_dashboard/create_tax_account"),
    createTxn: () => toast.info("Feature coming soon!", toast_position),
    viewDetails: () =>
      navigate(
        `/tax_account_dashboard/tax_account_view_all/tax_account_detail_page/${id}`,
        {
          replace: true,
        },
      ),
    dashboard: () => navigate("/tax_account_dashboard"),
  };

  useEffect(() => {
    dispatch(taxCompliantAccountDetail({ id }));
  }, [id, dispatch]);

  const primaryBtn = {
    enabledBgColor: "var(--text-primary)",
    enabledTextColor: "var(--positive-bg)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    imgColor: "var(--positive-bg)",
  };

  const outlineBtn = {
    enabledBgColor: "var(--bg-positive-bg)",
    enabledTextColor: "var(--text-primary)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    outLineColor: "var(--text-primary)",
  };

  const buttonRows = [
    [
      {
        ...primaryBtn,
        btnText: "Create account",
        icon: ICONS.SELECTUSER,
        handleFunction: handlers.createAccount,
      },
      {
        ...primaryBtn,
        btnText: "Create transaction",
        icon: ICONS.ADD_BUTTON,
        handleFunction: handlers.createTxn,
      },
      {
        ...primaryBtn,
        btnText: "View details",
        icon: ICONS.VIEW_DETAILS,
        handleFunction: handlers.viewDetails,
      },
    ],
    [
      {
        ...outlineBtn,
        btnText: "Go to dashboard",
        icon: ICONS.DASHBOARD,
        handleFunction: handlers.dashboard,
      },
    ],
  ];

  return (
    <div>
      <StatusContainer
        status="success"
        buttonRows={buttonRows}
        title={getSuccessTitle()}
      >
        <TabsWeapperSection title="Account details">
          <>
            {showOnlyUpdated ? (
              renderInfoCards()
            ) : (
              <>
                <InfoCard
                  bgColor="var(--positive-card)"
                  rows={[
                    {
                      leftTitle: accountData?.category,
                      leftSubTitle: "Assigned categories",
                    },
                  ]}
                />
                <InfoCard
                  bgColor="var(--positive-card)"
                  rows={[
                    {
                      leftTitle: accountNameStandardised(accountData?.name),
                      leftSubTitle: "Account name",
                    },
                  ]}
                />

                <InfoCard
                  bgColor="var(--positive-card)"
                  rows={[
                    {
                      leftTitle: accountData?.contact_no,
                      leftSubTitle: "Contact number",
                      icon: ICONS.CONTACT_NO,
                    },
                  ]}
                />
                <InfoCard
                  bgColor="var(--positive-card)"
                  rows={[
                    {
                      leftTitle: accountData?.email_id,
                      leftSubTitle: "Email ID",
                      icon: ICONS.GMAIL_RED_ERROR,
                    },
                  ]}
                />
              </>
            )}
          </>
        </TabsWeapperSection>
      </StatusContainer>
    </div>
  );
};

export default TaxationEditAccountSuccess;
